import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalUploader } from '@ff-os/pdp-shared-redux/slices/externalUploadersSlice';
import { toQrCode } from '@ff-os/shared-utils/to-qr-code';
import { usePrevious } from '../../../hooks/usePrevious';
import { Cell } from '../../grid/Cell';
import { Grid } from '../../grid/Grid';
import { Icon } from '../../icons/Icon';
import { Spinner } from '../../loaders/spinner/Spinner';
import styles from './DeviceUpload.module.scss';

export const DeviceUpload = ({
    status, imageCount,
    externalUploader, createExternalUploader,
    onChange
}: Props) => {

    const { t } = useTranslation();

    const [url, setUrl] = useState<string>();
    const previousImageCount = usePrevious(imageCount);
    const [countdown, setCountdown] = useState<{ hours: string; minutes: string; seconds: string }>();

    useEffect(() => {

        if (status !== 'ready') {
            return;
        }

        const ms = externalUploader ? externalUploader.expiresOn - Date.now() : 0;
        const timeoutId = setTimeout(createExternalUploader, ms);

        return () => clearTimeout(timeoutId);
    }, [status, externalUploader, createExternalUploader]);

    useEffect(() => {

        if (!externalUploader) {
            return;
        }

        toQrCode(externalUploader.url)
            .then(setUrl);

        const updateTimer = () => {

            const remaining = new Date(externalUploader.expiresOn - Date.now());
            const remainingSeconds = remaining.getSeconds();
            const remainingMinutes = remaining.getMinutes();
            const remainingHours = remaining.getHours() - 1; // Default hours is 1

            setCountdown({
                hours: padNumber(remainingHours, 2),
                minutes: padNumber(remainingMinutes, 2),
                seconds: padNumber(remainingSeconds, 2)
            });
        };

        const interval = setInterval(() => {
            updateTimer();
        }, 1000);

        updateTimer();

        return () => clearInterval(interval);
    }, [externalUploader]);

    useEffect(() => {
        if (previousImageCount === 0 && imageCount !== 0) {
            onChange();
        }
    }, [imageCount, onChange, previousImageCount]);

    if (!externalUploader || Date.now() > externalUploader.expiresOn) {
        return (
            <Grid direction='column' justify='center' align='center' gap='l' className={styles.window}>
                <Cell>
                    <Spinner spinnerSize='medium' overlaySize='full' />
                </Cell>
            </Grid>
        );
    }

    return (
        <Grid direction='column' justify='center' align='center' gap='l'>
            <Cell>
                <img crossOrigin='anonymous' src={url} alt='qr-code' />
            </Cell>
            <Cell>
                <p>
                    {t('windows.external_upload.open')}
                </p>
            </Cell>
            <Cell className={styles.textareaContainer} full>
                <div className={styles.textarea}>
                    {externalUploader.url}
                </div>
            </Cell>
            {countdown && (
                <Cell>
                    <p className={styles.countdown}>
                        {t('windows.external_upload.remaining', {
                            time: `${countdown.hours}:${countdown.minutes}:${countdown.seconds}`
                        })}
                    </p>
                </Cell>
            )}

            {imageCount > 0 && (
                <Cell>
                    <div className={styles.imageCountContainer}>
                        <Icon type='alert-circle-check' className={styles.check} />

                        <div>
                            <p className={styles.imageCount}>
                                {t('windows.external_upload.amount', { count: imageCount })}
                            </p>
                            <p className={styles.imageCountDevice}>
                                {t('windows.external_upload.from_device')}
                            </p>
                        </div>
                    </div>
                </Cell>
            )}
        </Grid>
    );
};

type Props = {
    status: 'pending' | 'ready';
    imageCount: number;
    externalUploader: ExternalUploader<any> | undefined;
    createExternalUploader: () => void;
    onChange: () => void;
};

const padNumber = (number: number, minimumDigits: number, language?: string) => {
    return number.toLocaleString(language, { minimumIntegerDigits: minimumDigits });
};